import { takeEvery, takeLatest, call, put, select, cancelled } from 'redux-saga/effects';
import { setStates, setSuppliers, fetchError, setChartsData, setPricingDetail } from '../actions';
import { FETCH_STATES, FETCH_SUPPLIERS, SET_SELECTED_STATE, FETCH_CHARTSDATA,FETCH_PRICING_DETAIL } from '../types';
import { fetchStatesApi, fetchLoadZonesApi, fetchRateClassesApi, fetchPriceTrendChartData, fetchPriceTrendMinMaxChartData, fetchForwardCurveChartData, fetchForwadCurveMinMaxChartData, fetchPricingDetailApi } from '../../api';
import {RateClassifications} from '../../const/index'

function* fetchAndDispatchChart(apiFn, chartType, params) {
  const controller = new AbortController();
  try {
    // Set loading to true when starting to fetch
    yield put(setChartsData(null, chartType, true));
    const response = yield call(apiFn, { ...params, signal: controller.signal });
    // Set loading to false when data is received
    yield put(setChartsData(response, chartType, false));
  } catch (error) {
    if (error?.name === 'AbortError') {
      // silently ignore aborted requests
    } else {
      console.error(`Error fetching ${chartType}:`, error);
      // Set loading to false on error
      yield put(setChartsData(null, chartType, false));
    }
  } finally {
    if (yield cancelled()) {
      controller.abort();
    }
  }
}

const formatMMDDYY = (d: Date) => {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
};

// Raw shapes match what reducers expect:
// - convertToApexChartsPlots expects: [{ Supplier, EnerexSupplierIdentifier, Prices: [{ Date, Price }] }]
// - convertToApexChartsMinMaxPlots expects: [{ EnerexSupplierIdentifier, Prices: [{ Date, Supplier, Price, MinPrice, MaxPrice, MeanPrice, MedianPrice }] }]
const buildStaticPriceTrendRaw = () => {
  const base = new Date();
  const dates = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(base);
    d.setMonth(d.getMonth() - (5 - i));
    d.setDate(1);
    return formatMMDDYY(d);
  });

  return [
    {
      Supplier: "ENGIE",
      EnerexSupplierIdentifier: "6BD2C20F-0CE3-4B00-ACDE-63569A4273A1",
      Prices: dates.map((Date, idx) => ({
        Date,
        Price: 0.12 + idx * 0.01,
      })),
    },
    {
      Supplier: "New Wave Energy",
      EnerexSupplierIdentifier: "8F63A33F-41DB-4129-B0B4-1A59370D0403",
      Prices: dates.map((Date, idx) => ({
        Date,
        Price: 0.1 + idx * 0.008,
      })),
    },
  ];
};

const buildStaticPriceTrendMinMaxRaw = () => {
  const base = new Date();
  const dates = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(base);
    d.setMonth(d.getMonth() - (5 - i));
    d.setDate(1);
    return formatMMDDYY(d);
  });

  const supplierId = "6BD2C20F-0CE3-4B00-ACDE-63569A4273A1";
  const supplierName = "ENGIE";

  return [
    {
      EnerexSupplierIdentifier: supplierId,
      Prices: dates.map((Date, idx) => {
        const price = 0.13 + idx * 0.01;
        return {
          Date,
          Supplier: supplierName,
          Price: price,
          MinPrice: price * 0.9,
          MaxPrice: price * 1.1,
          MeanPrice: price,
          MedianPrice: price,
        };
      }),
    },
  ];
};

function* fetchChartsData(action: any) {
  try {
    const {clientId, selectedClientIdentifier, userToken, IsNotAnonymized, isSystemAdmin, isPremium} = action.payload || {};
    const selectedMarket = yield select((state) => state.customPricing.selectedMarket);
    // Prefer last applied commodity from Redux; fallback to selectedMarket commodity
    const appliedCommodity = yield select((state) => state.customPricing.commodity);
    const commodity = (() => {
      if (appliedCommodity === 1 || appliedCommodity === 2) {
        return appliedCommodity;
      }
      const marketCommodity = selectedMarket?.commodity;
      if (marketCommodity === 'Electricity' || marketCommodity === 'Electric' || marketCommodity === 'electric' || marketCommodity === 1 || marketCommodity === '1') {
        return 1;
      }
      if (marketCommodity === 'NatGas' || marketCommodity === 'Gas' || marketCommodity === 'Nat Gas' || marketCommodity === 2 || marketCommodity === '2') {
        return 2;
      }
      return '';
    })();
    // const fullstate = yield select((state) => state.customPricing);
    let MarketEnerexIdentifier=yield select((state) => state.customPricing.selectedMarket.value);
    MarketEnerexIdentifier = commodity === 2 ? null : MarketEnerexIdentifier;
    // const fullstate = yield select((state) => state.customPricing);
    const fromDate = yield select((state) => state.customPricing.fromDate);
    const toDate = yield select((state) => state.customPricing.toDate);
    const effectiveFromDate = yield select((state) => state.customPricing.effectiveFromDate);
    const effectiveToDate = yield select((state) => state.customPricing.effectiveToDate);
    const startFromDate = yield select((state) => state.customPricing.startFromDate);
    const startToDate = yield select((state) => state.customPricing.startToDate);
    const selectedStateName = yield select(s => s.customPricing.selectedState?.name);
    const statesList = yield select((state) => state.customPricing.states);
    const isAllSelected = selectedStateName === 'All' || !selectedStateName;
    const selectedStatesArray = isAllSelected ? []
      // ? (statesList || [])
      //     .filter((s: any) => s && s.name && s.name !== 'All')
      //     .map((s: any) => s.name)
      : [selectedStateName];
    // Term is now a number input, get it directly from selectedTerm in Redux state
    const selectedTerm = yield select((state) => state.customPricing.selectedTerm);
    // If explicitly cleared (empty string), send empty string. If null/undefined (initial load), send empty string (no default)
    const termValue = selectedTerm === "" ? "" : (selectedTerm && selectedTerm !== null && selectedTerm !== undefined ? String(selectedTerm) : "");
    const ProductType = yield select((state) => state.customPricing.productTypes);
    // const selectedProductType = ProductType.find((state: any) => state?.isSelected)?.value;
    const selectedProductType =
  ProductType
    ?.filter((state: any) => state?.isSelected)
    ?.map((state: any) => state.value) || [];
 const costComponent = [];
//  yield select((state) => state.customPricing.costComponents);
    // const selectedProductType = ProductType.find((state: any) => state?.isSelected)?.value;
    const rawSelectedPassThrough =
  costComponent
    ?.filter((state: any) => state?.isSelected)
    ?.map((state: any) => state.value) || [];
    const hasAllSelected = rawSelectedPassThrough.includes("All");
    const passThroughValues = hasAllSelected
      ? []
      : rawSelectedPassThrough.filter((value: any) => value !== "All");
    //dependant dropdowns values
    const selectedUtility       = yield select(s => s.customPricing.selectedUtility?.value);
    const selectedRateClass     = yield select(s => s.customPricing.selectedRateClass?.name);
    const selectedLoadProfile   = yield select(s => s.customPricing.selectedLoadProfile?.value);
    const selectedLoadZone      = yield select(s => s.customPricing.selectedLoadZone?.name);
    const selectedBillingMethod = yield select(s => s.customPricing.selectedBillingMethod?.name);
    const noOfMonths = null;
    // yield select((state) => state.customPricing.noOfMonths);
    const usage = yield select(s => s.customPricing.usage); // legacy
    const usageFrom = yield select(s => s.customPricing.usageFrom);
    const usageTo = yield select(s => s.customPricing.usageTo);
    const demand = null;
    // yield select(s => s.customPricing.demand);
    const loadFactor = yield select(s => s.customPricing.loadFactor); // legacy
    const loadFactorFrom = yield select(s => s.customPricing.loadFactorFrom);
    const loadFactorTo = yield select(s => s.customPricing.loadFactorTo);
    const customerClass = yield select(s => s.customPricing.customerClass);
    // When "All" is selected, customerClass will be "" (empty string), otherwise send the value
    const customerClassParam = customerClass && customerClass !== "" ? customerClass : "";

    // Pass usageFrom/usageTo as-is: 0 is a valid filter value, null means not set
    // Form validation already handles converting cleared fields to null
    if (!userToken) {
      console.warn('Missing user token for custom pricing chart request');
      return;
    }

    // If user is explicitly not premium, do not call chart APIs at all.
    // (Charts will render with a deterministic static dataset instead.)
    if (isPremium === false) {
      yield put(setChartsData(buildStaticPriceTrendRaw(), "priceTrend", false));
      yield put(setChartsData(buildStaticPriceTrendMinMaxRaw(), "priceTrendMinMax", false));
      return;
    }

    const params = {
      userToken,
      commodity,
      selectedStates: selectedStatesArray,
      selectedTerm: termValue,
      fromDate,
      toDate,
      effectiveFromDate,
      effectiveToDate,
      startFromDate,
      startToDate,
      selectedUtility,
      selectedRateClass,
      selectedLoadProfile,
      selectedLoadZone,
      selectedBillingMethod,
      usage,
      usageFrom,
      usageTo,
      demand,
      loadFactor,
      loadFactorFrom,
      loadFactorTo,
      clientId,
      selectedClientIdentifier,
      selectedProductType,
      passThrough: passThroughValues,
      noOfMonths,
      MarketEnerexIdentifier,
      IsNotAnonymized,
      isSystemAdmin,
      customerType: customerClassParam
    };
    yield call(fetchAndDispatchChart, fetchPriceTrendChartData, 'priceTrend', params);
    yield call(fetchAndDispatchChart, fetchPriceTrendMinMaxChartData, 'priceTrendMinMax', params);
    // yield call(fetchAndDispatchChart, fetchForwardCurveChartData, 'forwardCurve', params);
    // yield call(fetchAndDispatchChart, fetchForwadCurveMinMaxChartData, 'forwardCurveMinMax', params);

  } catch (error) {
    yield put(fetchError('Failed to fetch chart data'));
  }
}
function* fetchPricingDetailSaga(action: any) {
  try {
    yield put(setPricingDetail(null, true));

    const {
      clientId,
      selectedClientIdentifier,
      userToken,
      IsNotAnonymized,
      isSystemAdmin,
      bookedDate,
      supplierId,
    } = action.payload || {};

    if (!userToken || !bookedDate || !supplierId) {
      console.warn('Missing data for pricing detail request', {
        hasToken: !!userToken,
        bookedDate,
        supplierId,
      });
      yield put(setPricingDetail(null, false));
      return;
    }

    // ---------- REUSE FILTERS FROM REDUX (same as fetchChartsData) ----------
    const selectedMarket = yield select((state) => state.customPricing.selectedMarket);
    // Prefer last applied commodity from Redux; fallback to selectedMarket commodity
    const appliedCommodity = yield select((state) => state.customPricing.commodity);
    const commodity = (() => {
      if (appliedCommodity === 1 || appliedCommodity === 2) {
        return appliedCommodity;
      }
      const marketCommodity = selectedMarket?.commodity;
      if (marketCommodity === 'Electricity' || marketCommodity === 'Electric' || marketCommodity === 'electric' || marketCommodity === 1 || marketCommodity === '1') {
        return 1;
      }
      if (marketCommodity === 'NatGas' || marketCommodity === 'Gas' || marketCommodity === 'Nat Gas' || marketCommodity === 2 || marketCommodity === '2') {
        return 2;
      }
      return '';
    })();

    let MarketEnerexIdentifier = yield select((state) => state.customPricing.selectedMarket.value);
    MarketEnerexIdentifier = commodity === 2 ? null : MarketEnerexIdentifier;

    const fromDate = yield select((state) => state.customPricing.fromDate);
    const toDate = yield select((state) => state.customPricing.toDate);
    const effectiveFromDate = yield select((state) => state.customPricing.effectiveFromDate);
    const effectiveToDate = yield select((state) => state.customPricing.effectiveToDate);
    const startFromDate = yield select((state) => state.customPricing.startFromDate);
    const startToDate = yield select((state) => state.customPricing.startToDate);

    const selectedStateName = yield select((s) => s.customPricing.selectedState?.name);
    const isAllSelected = selectedStateName === 'All' || !selectedStateName;
    const selectedStatesArray = isAllSelected ? [] : [selectedStateName];

    const selectedTerm = yield select((state) => state.customPricing.selectedTerm);
    const termValue =
      selectedTerm === ''
        ? ''
        : selectedTerm && selectedTerm !== null && selectedTerm !== undefined
        ? String(selectedTerm)
        : '';

    const ProductType = yield select((state) => state.customPricing.productTypes);
    const selectedProductType =
      ProductType?.filter((state: any) => state?.isSelected)?.map((state: any) => state.value) || [];

    const costComponent = [];
    const rawSelectedPassThrough =
      costComponent?.filter((state: any) => state?.isSelected)?.map((state: any) => state.value) || [];
    const hasAllSelected = rawSelectedPassThrough.includes('All');
    const passThroughValues = hasAllSelected
      ? []
      : rawSelectedPassThrough.filter((value: any) => value !== 'All');

    const selectedUtility = yield select((s) => s.customPricing.selectedUtility?.value);
    const selectedRateClass = yield select((s) => s.customPricing.selectedRateClass?.name);
    const selectedLoadProfile = yield select((s) => s.customPricing.selectedLoadProfile?.value);
    const selectedLoadZone = yield select((s) => s.customPricing.selectedLoadZone?.name);
    const selectedBillingMethod = yield select((s) => s.customPricing.selectedBillingMethod?.name);

    const noOfMonths = null;
    const usage = yield select((s) => s.customPricing.usage);
    const usageFrom = yield select((s) => s.customPricing.usageFrom);
    const usageTo = yield select((s) => s.customPricing.usageTo);
    const demand = null;
    const loadFactor = yield select((s) => s.customPricing.loadFactor);
    const customerClass = yield select((s) => s.customPricing.customerClass);
    const customerClassParam = customerClass && customerClass !== '' ? customerClass : '';
    // customerClass is included in params to populate CustomerClass in basePayload, but CustomerType is excluded from final payload

    const params = {
      userToken,
      commodity,
      selectedStates: selectedStatesArray,
      selectedTerm: termValue,
      fromDate,
      toDate,
      effectiveFromDate,
      effectiveToDate,
      startFromDate,
      startToDate,
      selectedUtility,
      selectedRateClass,
      selectedLoadProfile,
      selectedLoadZone,
      selectedBillingMethod,
      usage,
      usageFrom,
      usageTo,
      demand,
      loadFactor,
      clientId,
      selectedClientIdentifier,
      selectedProductType,
      passThrough: passThroughValues,
      noOfMonths,
      MarketEnerexIdentifier,
      IsNotAnonymized,
      isSystemAdmin,
      customerType: customerClassParam,
      bookedDate,
      supplierId,
    };

    const response = yield call(fetchPricingDetailApi, params);

    yield put(setPricingDetail(response, false));
  } catch (error) {
    yield put(setPricingDetail(null, false));
  }
}

// function* fetchUtilitiesSaga(action: any) {
//   try {
//     const { state, commodity } = action.payload;

//     const token = yield select((state) => state.customPricing.ECToken?.token);
//     const params = {
//       state,
//       commodity,
//       token
//     }
//     // Pass commodity through to your API client:
//     const list  = yield call(
//       fetchUtilitiesApi,
//       params
//     );
//     yield put(setUtilities(list));
//   } catch (err) {
//     yield put(fetchError('Failed to fetch utilities'));
//   }
// }

export function* watchCustomPricingActions() {
//   yield takeEvery(FETCH_STATES, fetchStatesSaga);
//   yield takeEvery(FETCH_STATES, fetchStatesSaga);
//   yield takeEvery(SET_SELECTED_STATE, fetchLoadZonesSaga);
  // Cancel any in-flight chart fetch when a new one starts
  yield takeLatest(FETCH_CHARTSDATA, fetchChartsData);
  yield takeLatest(FETCH_PRICING_DETAIL, fetchPricingDetailSaga);
  // yield takeEvery(FETCH_UTILITIES, fetchUtilitiesSaga);
//   yield takeEvery(FETCH_SUPPLIERS, fetchSuppliersSaga);
//   yield takeEvery(FETCH_RATE_CLASSES, fetchRateClassesSaga);
}

// TODO: either remove or use these saga as per future requirements

// function* fetchSuppliersSaga(action: any) {
//   try {
//     const response = yield call(fetchSuppliersApi, action.payload);
//     yield put(setSuppliers(response));
//   } catch (error) {
//     yield put(fetchError('Failed to fetch suppliers'));
//   }
// }

// function* fetchRateClassesSaga(action: any) {
//   try {
//     const response = yield call(fetchRateClassesApi, action.payload);
//     yield put(setRateClasses(response));
//   } catch (error) {
//     yield put(fetchError('Failed to fetch rate classes'));
//   }
// }

// function* fetchStatesSaga() {
//   try {
//     const response = yield call(fetchStatesApi);
//     yield put(setStates(response));
//   } catch (error) {
//     yield put(fetchError('Failed to fetch states'));
//   }
// }

// function* fetchRateClassesSaga() {
//     try {
//       const rateClasesList = RateClassifications.map((rateClass) => { return {...rateClass, isSelected: false}});
//       yield put(setRateClasses(rateClasesList));
//     } catch (error) {
//       yield put(fetchError('Failed to fetch states'));
//     }
//   }

// function* fetchUtilitiesSaga(action: any) {
//   try {
//     const response = yield call(fetchUtilitiesApi, action.payload);
//     yield put(setUtilities(response));
//   } catch (error) {
//     yield put(fetchError('Failed to fetch utilities'));
//   }
// }

// function* fetchLoadZonesSaga(action: any) {
//   try {
//     const response = yield call(fetchLoadZonesApi, action.payload);
//     yield put(setLoadZones(response));
//   } catch (error) {
//     yield put(fetchError('Failed to fetch load zones'));
//   }
// }
